import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Loader2, ExternalLink, AlertCircle, RefreshCcw, Database,
} from 'lucide-react';
import FFLoader from '../components/FFLoader';
import { formsApi } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('ff_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Per-form Airtable configuration page.
 *
 * Two creds: PAT + base URL (which contains the table id when copied
 * from Airtable's "Copy link to view"). After save we show the
 * auto-discovered field schema so users see which Airtable columns
 * we'll write to.
 */
export default function AirtableIntegrationPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const formList = await formsApi.getAll();
        const formsArray = Array.isArray(formList) ? formList : (formList?.forms || []);
        const decorated = formsArray.map((f) => {
          const at = (f?.settings?.airtable) || {};
          return {
            ...f,
            _at: {
              tokenInput: '',
              urlInput: '',
              enabled: !!at.enabled,
              configured: !!(at.token && at.base_id && at.table_id),
              maskedToken: at.token ? `…${at.token.slice(-4)}` : null,
              tableName: at.table_name || null,
              fields: at.fields || [],
              lastError: at.last_error || null,
              saving: false,
              testing: false,
            },
          };
        });
        setForms(decorated);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load Airtable settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patchForm = (id, patch) => setForms((prev) => prev.map((f) =>
    f.id === id ? { ...f, _at: { ...f._at, ...patch } } : f
  ));

  const handleTest = async (form) => {
    const token = (form._at.tokenInput || '').trim();
    const base_url = (form._at.urlInput || '').trim();
    if (!token || !base_url) { toast.error('Paste both the token and base URL first'); return; }
    patchForm(form.id, { testing: true });
    try {
      const { data } = await axios.post(
        `${API}/api/airtable/test`,
        { token, base_url },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success(`Connected to "${data.table_name}" (${data.fields.length} writable columns)`);
      patchForm(form.id, { tableName: data.table_name, fields: data.fields });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reach Airtable');
    } finally {
      patchForm(form.id, { testing: false });
    }
  };

  const handleSave = async (form) => {
    const token = (form._at.tokenInput || '').trim();
    const base_url = (form._at.urlInput || '').trim();
    if (!token || !base_url) { toast.error('Paste both the token and base URL first'); return; }
    patchForm(form.id, { saving: true });
    try {
      const { data } = await axios.put(
        `${API}/api/airtable/forms/${form.id}`,
        { enabled: true, token, base_url },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success('Connected — new submissions will create Airtable records');
      patchForm(form.id, {
        enabled: true,
        configured: true,
        maskedToken: data.token_masked || null,
        tableName: data.table_name || null,
        fields: data.fields || [],
        tokenInput: '',
        urlInput: '',
        lastError: null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save Airtable connection');
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  const handleDisconnect = async (form) => {
    if (!window.confirm('Disconnect Airtable? New submissions will no longer create Airtable records.')) return;
    patchForm(form.id, { saving: true });
    try {
      await axios.put(
        `${API}/api/airtable/forms/${form.id}`,
        { enabled: false },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success('Disconnected');
      patchForm(form.id, {
        enabled: false, configured: false, maskedToken: null,
        tableName: null, fields: [], tokenInput: '', urlInput: '', lastError: null,
      });
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  const handleToggle = async (form, checked) => {
    if (!form._at.configured) {
      toast.error('Save a connection first, then toggle it on.');
      return;
    }
    patchForm(form.id, { saving: true });
    try {
      await axios.put(
        `${API}/api/airtable/forms/${form.id}`,
        { enabled: checked },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      patchForm(form.id, { enabled: checked });
      toast.success(checked ? 'Airtable sync resumed' : 'Airtable sync paused');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
      patchForm(form.id, { enabled: !checked });
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <FFLoader size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="airtable-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/integrations" className="p-2 hover:bg-zinc-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#F82B60] rounded-lg flex items-center justify-center">
              <Database className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-bold text-zinc-900">Airtable</h1>
            <Badge variant="secondary" className="ml-2">Free</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">How it works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="text-sm text-zinc-700 space-y-2 list-decimal list-inside">
              <li>
                Open{' '}
                <a
                  href="https://airtable.com/create/tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline inline-flex items-center gap-0.5"
                >
                  airtable.com/create/tokens <ExternalLink className="w-3 h-3" />
                </a>{' '}
                → <strong>Create new token</strong>. Name it (e.g. <em>Fantastic Forms</em>).
              </li>
              <li>
                Add scopes: <code className="bg-zinc-100 px-1 rounded text-xs">data.records:read</code>, <code className="bg-zinc-100 px-1 rounded text-xs">data.records:write</code>, <code className="bg-zinc-100 px-1 rounded text-xs">schema.bases:read</code>.
              </li>
              <li>Under <strong>Access</strong>, add the specific base(s) you want submissions to land in. Click <strong>Create token</strong> and copy the value (starts with <code className="bg-zinc-100 px-1 rounded text-xs">pat</code>).</li>
              <li>In Airtable, open the table you want submissions in, click <strong>Share view</strong> → <strong>Copy link to view</strong>, and paste both values below.</li>
            </ol>
            <p className="text-xs text-zinc-500">
              Form questions auto-match Airtable columns by name (case-insensitive). Computed columns (formula, rollup, lookup, count, etc.) are skipped automatically — we only write to writable fields. Select / multiple-select options are auto-created on the fly if missing.
            </p>
          </CardContent>
        </Card>

        <section className="space-y-3" data-testid="forms-section">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-zinc-900">Your forms</h2>
            <span className="text-xs text-zinc-500">{forms.length} {forms.length === 1 ? 'form' : 'forms'}</span>
          </div>

          {forms.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-zinc-500">
              No forms yet. <Link to="/dashboard" className="text-zinc-900 underline">Go to dashboard</Link>.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {forms.map((form) => (
                <Card key={form.id} data-testid={`form-row-${form.id}`}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{form.title || 'Untitled form'}</p>
                        {form._at.configured ? (
                          <p className="text-xs text-zinc-500 mt-0.5">
                            Connected to table <strong>{form._at.tableName}</strong> · token <code className="px-1 bg-zinc-100 rounded">{form._at.maskedToken}</code>
                          </p>
                        ) : (
                          <p className="text-xs text-zinc-400 mt-0.5">Not connected</p>
                        )}
                        {form._at.lastError && (
                          <p className="text-xs text-red-600 mt-1 inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Last sync failed: {form._at.lastError}
                          </p>
                        )}
                      </div>
                      {form._at.configured && (
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-zinc-500">{form._at.enabled ? 'Active' : 'Paused'}</span>
                          <Switch
                            checked={form._at.enabled}
                            onCheckedChange={(c) => handleToggle(form, c)}
                            data-testid={`airtable-toggle-${form.id}`}
                          />
                        </div>
                      )}
                    </div>

                    {form._at.configured && form._at.fields.length > 0 && (
                      <div className="rounded-md border border-zinc-100 bg-zinc-50 p-3" data-testid="airtable-fields">
                        <p className="text-xs text-zinc-500 mb-2">Writable Airtable columns ({form._at.fields.length})</p>
                        <div className="flex flex-wrap gap-1.5">
                          {form._at.fields.map((p) => (
                            <span
                              key={p.name}
                              className="inline-flex items-baseline gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white text-zinc-700 border border-zinc-200"
                            >
                              {p.name}
                              <span className="text-zinc-400">·</span>
                              <span className="text-zinc-500">{p.type}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-zinc-500">
                          Personal Access Token{form._at.configured ? ' (paste a new one to replace)' : ''}
                        </Label>
                        <Input
                          placeholder="pat..."
                          value={form._at.tokenInput}
                          onChange={(e) => patchForm(form.id, { tokenInput: e.target.value })}
                          data-testid={`airtable-token-${form.id}`}
                          className="font-mono text-xs"
                          type="password"
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Base / Table URL</Label>
                        <Input
                          placeholder="https://airtable.com/appXXXX/tblYYYY/..."
                          value={form._at.urlInput}
                          onChange={(e) => patchForm(form.id, { urlInput: e.target.value })}
                          data-testid={`airtable-url-${form.id}`}
                          className="font-mono text-xs"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(form)}
                        disabled={form._at.testing || form._at.saving}
                        data-testid={`airtable-test-${form.id}`}
                      >
                        {form._at.testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
                        Test
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSave(form)}
                        disabled={form._at.saving || form._at.testing}
                        data-testid={`airtable-save-${form.id}`}
                      >
                        {form._at.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        {form._at.configured ? 'Replace' : 'Save'}
                      </Button>
                      {form._at.configured && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect(form)}
                          disabled={form._at.saving}
                          data-testid={`airtable-disconnect-${form.id}`}
                        >
                          Disconnect
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
